import Vue from 'vue'
import axios from 'axios'
import VueAxios from 'vue-axios'
import ConfigAPI from '@/config/api'
import Session from '@/handler/session'
import Storage from '@/handler/storageLocal'

// Patrón de facto (Google Web Fundamentals) para convertir la VAPID public key
// de base64url a Uint8Array, tal como lo espera PushManager.subscribe().
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(window.navigator.userAgent)
}

function getCachedConfig() {
  try {
    return JSON.parse(Storage.getValue('ConfigurationNotificationsPush'))
  } catch (e) {
    return null
  }
}

function fetchConfig() {
  Vue.use(VueAxios, axios)
  const instance = axios.create()

  return instance.get(ConfigAPI.baseURL + "public/notifications/push/1/configuration")
    .then((response) => {
      Storage.setValue('ConfigurationNotificationsPush', JSON.stringify(response.data))
      return response.data
    })
}

function setSubscriptionSentToServer(sent) {
  window.sessionStorage.setItem(window.location.origin + '_NotificationsSentToServer', sent ? 1 : 0)
}
function isSubscriptionSentToServer() {
  return window.sessionStorage.getItem(window.location.origin + '_NotificationsSentToServer') == 1
}

// Persistente (no por pestaña como isSubscriptionSentToServer) — registra que el
// usuario desactivó las notificaciones a propósito desde el menú, para que la
// suscripción automática no se la pise sola en la próxima carga de página.
function setUserOptedOut(optedOut) {
  if (optedOut) {
    Storage.setValue('PushNotificationsOptOut', '1')
  } else {
    Storage.removeValue('PushNotificationsOptOut')
  }
}
function isUserOptedOut() {
  return Storage.getValue('PushNotificationsOptOut') === '1'
}
function saveSubscription(pushSubscription) {
  if (!Session.getToken()) {
    return Promise.reject(new Error('No se puede suscribir: no hay una sesión iniciada.'))
  }

  var url = ConfigAPI.baseURL + "public/notifications/push/1/subscribe" + Session.getToken()

  var data = Object.assign({}, pushSubscription.toJSON(), {
    source: 'SYSTEM',
    version: window.navigator.userAgent,
    isMobile: isMobileDevice(),
  })

  Vue.use(VueAxios, axios)

  const instance = axios.create()

  return instance.post(url, data)
    .then(() => {
      console.log("Dispositivo subscripto")
    })
}
function removeSubscription(pushSubscription) {
  var url = ''
  if (Session.getToken()) {
    url = ConfigAPI.baseURL + "public/notifications/push/1/unsubscribe" + Session.getToken()
  } else {
    url = ConfigAPI.baseURL + "public/notifications/push/1/unsubscribe"
  }

  Vue.use(VueAxios, axios)

  const instance = axios.create()

  return instance.post(url, { endpoint: pushSubscription.endpoint })
}

// El update de versión de la app desregistra el Service Worker (ver
// clearServiceWorkerAndCaches en main.js) — eso invalida la PushSubscription
// del navegador (su vida está atada a la ServiceWorkerRegistration que la
// creó). Sin este reset, isSubscriptionSentToServer() seguía en true después
// del reload (vive en sessionStorage, sobrevive al location.replace) y
// autoSubscribeIfNeeded() se salteaba la re-suscripción creyendo que ya
// estaba hecha: el usuario quedaba con el toggle apagado y sin push hasta
// que lo reactivaba a mano. Se llama desde clearServiceWorkerAndCaches(),
// antes de desregistrar, para que la re-suscripción automática de siempre
// vuelva a correr tras el reload — no toca isUserOptedOut(), así que a quien
// desactivó push a propósito no se le vuelve a activar solo.
//
// También se invalida acá el caché de la configuración (vapidPublicKey):
// enablePush()/autoSubscribeIfNeeded() solo van a buscarla fresca al backend
// si no hay NADA cacheado — si en algún momento se regeneran las claves
// VAPID desde el panel, un dispositivo con la config vieja cacheada se
// re-suscribiría igual (no tira error), pero con la clave pública vieja: el
// envío falla recién del lado del servidor (mismatch de VAPID), en
// silencio. Forzar un fetch de config fresca en cada re-suscripción evita
// ese desfasaje sin tener que acordarse de limpiar el caché a mano cada vez
// que se rota la clave.
export function invalidatePushSubscriptionState() {
  setSubscriptionSentToServer(false)
  Storage.removeValue('ConfigurationNotificationsPush')
}

// Suscripción automática y silenciosa al cargar la app (comportamiento histórico).
// Se invoca explícitamente desde main.js — no corre solo con solo importar el módulo.
export function autoSubscribeIfNeeded() {
  if (!Session.getToken()) return

  const config = getCachedConfig()
  if (!config || !config.vapidPublicKey) return
  if (isUserOptedOut()) return

  navigator.serviceWorker.ready
    .then(function(registration) {
      if (isSubscriptionSentToServer()) {
        console.log('La suscripción ya fue realizada.')
        return
      }

      registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.vapidPublicKey),
      })
        .then(function(pushSubscription) {
          saveSubscription(pushSubscription)
          setSubscriptionSentToServer(true)
        })
        .catch(function(err) {
          console.log('No se puede obtener permisos para notificar.', err)
          setSubscriptionSentToServer(false)
        })
    })
    .catch(function(err) {
      console.log('Service Worker no disponible.', err)
    })
}

// Soporte del navegador + estado del permiso, para reflejarlo en el menú de usuario.
// Devuelve 'granted' | 'denied' | 'default' | 'unsupported'.
export function getPushPermissionStatus() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return 'unsupported'
  }
  return Notification.permission
}

export function isPushSubscribed() {
  if (!('serviceWorker' in navigator)) return Promise.resolve(false)

  return navigator.serviceWorker.ready
    .then((registration) => registration.pushManager.getSubscription())
    .then((subscription) => !!subscription)
    .catch(() => false)
}

// El sistema puede tener el feature de push configurado pero inactivo
// (ver pantalla de administración) — en ese caso no tiene sentido ofrecer el toggle.
export async function isPushSystemActive() {
  let config = getCachedConfig()
  if (!config || typeof config.active === 'undefined') {
    try {
      config = await fetchConfig()
    } catch (e) {
      return false
    }
  }
  return !!(config && config.active)
}

// Alternativa manual a la suscripción automática — pensada para el menú de usuario,
// por si el usuario canceló o bloqueó por error el prompt nativo del navegador.
export async function enablePush() {
  let config = getCachedConfig()
  if (!config || !config.vapidPublicKey) {
    config = await fetchConfig()
  }
  if (!config || !config.vapidPublicKey) {
    throw new Error('No se pudo obtener la configuración de notificaciones push.')
  }

  const registration = await navigator.serviceWorker.ready
  const pushSubscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(config.vapidPublicKey),
  })

  await saveSubscription(pushSubscription)
  setSubscriptionSentToServer(true)
  setUserOptedOut(false)

  return pushSubscription
}

export async function disablePush() {
  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()

  if (subscription) {
    await removeSubscription(subscription)
    await subscription.unsubscribe()
  }

  setSubscriptionSentToServer(false)
  setUserOptedOut(true)
}
